import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../i18n";
import store from "../../../../store";
import router from "../../../../router";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
    watchRulesChildren
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import { has, have } from "../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionOpenUrl,
    createRowActionReStatus,
    createRowActionView
} from "../../../../utils/page";
import { flatWhereGroup } from "../../../../utils/search";
import { required, requiredWhen, validateError } from "../../../../utils/validate";
import { createForm as createFormHazLicense } from "../../haz-license/haz-license/haz-license.logic";
import { createConditionAction, createConditionForm } from "../../haz-license/request/request.condition";
import { callUserObjective, createColumns, createField } from "../../haz-license/request/request.section";
import { callSpeciesByGroup } from "../../master-mgt/haz-object/haz-object.section";
import { openModalForm as openModalFormConfirmApprove } from "../../storage-location/request/modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmReceive } from "../../storage-location/request/modal/confirm-receive.logic";
import { openModalForm as openModalFormConfirmRequest } from "../../storage-location/request/modal/confirm-request.logic";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import {
    callRequestDocGenType,
    callUser,
    callUserCompanyMe,
    checkUserCompanyOperation
} from "../../storage-location/request/requestor/requestor.section";


const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callReportHazLicense = ({ data }) => store.dispatch(`${actionPrefix}/reportHazLicense`, { data });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refHazLicenseTemp.refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refHazLicenseTemp.refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refHazLicenseTemp.refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const validate = ({ props, data, refs }) => {
    const { menuContext } = props;
    const modelRef = data.useForm?.modelRef || data.form;

    const rulesTop = createRulesTop({ props, data, refs });
    const rules1 = createRulesTab1({ props, data, refs });
    const rules2 = createRulesTab2({ props, data, refs });
    const rules311 = createRulesTab31({ props, data, refs, isLocation: false });
    const rules312 = createRulesTab31({ props, data, refs, isLocation: true });
    const rules5 = createRulesTab5({ props, data, refs });
    let tabKey = null;
    return data.useForm
        .validate(Object.keys(rulesTop))
        .then(() => {
            tabKey = "Requestor";
            return data.useForm.validate(Object.keys(rules1));
        })
        .then(() => {
            tabKey = "HazLicenseDetail";
            return data.useForm.validate(Object.keys(rules2));
        })
        .then(() => {
            tabKey = "StorageLocationSub";
            if(modelRef.refHazLicenseTemp.refOperation?.key === "4") {
                return data.useForm.validateField(Object.keys(rules311)[0], null, Object.values(rules311)[0], {});
            }
            return Promise.resolve();
        })
        .then(() => {
            tabKey = "StorageLocation";
            return data.useForm.validateField(Object.keys(rules312)[0], null, Object.values(rules312)[0], {});
        })
        .then(() => {
            tabKey = "Attachment";
            return data.useForm.validate(Object.keys(rules5));
        })
        .catch(error => {
            if (tabKey) {
                menuContext.tabKey = tabKey;
            }
            validateError({ error });
            throw error;
        });
};

export const onClickSave = ({ props, data, refs, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**",
                "refHazLicense.id",
                "refHazLicense.approveCode",
                "refHazLicense.refUserCompany.*",
                "refHazLicense.refUserCompany.refPersonType.*",
                "refHazLicenseTemp.**",
                "refHazLicenseTemp.refUserCompany.**",
                "refHazLicenseTemp.refUser.**",
                "refHazLicenseNew.id",
                "refHazLicenseNew.approveCode",
                "refHazLicenseNew.expireDate",
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "refHazLicenseTemp.refUserCompany.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refHazLicenseTemp.refUserCompany.userObjectives",
                    select: ["*", "refObjective.*"],
                    page: 0,
                    size: 1000000,
                },
                {
                    name: "refHazLicenseTemp.refUser.addresses",
                    select: ["*"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                },
                {
                    name: "refHazLicenseTemp.hazLicenseDetails",
                    select: ["*", "refHaz.refOperation.*", "refHaz.refHazObject.refSpecies.*", "refHaz.refManufactureCountry.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }]
                },
                {
                    name: "refHazLicenseTemp.hazLicenseStorageLocations",
                    select: ["*", "refStorageLocation.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: [
                        {
                            name: "refStorageLocation.subs",
                            select: ["**"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }],
                            children: []
                        },
                        {
                            name: "refStorageLocation.addresses",
                            select: ["**"],
                            page: 0,
                            size: 1000000,
                            sort: [{ name: "createDate", type: "ASC" }],
                            children: []
                        }
                    ]
                },
                {
                    name: "refHazLicenseTemp.hazLicenseCountries",
                    select: ["**"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.waitApprove",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitApprove" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forReceiveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "feeStatements",
                    select: ["*", "refType.*", "refStatus.*"],
                    page: 0,
                    size: 1,
                    sort: [{ name: "createDate", type: "DESC" }],
                    children: []
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forRequest" })) {
        whereGroup.push([{ name: "refHazLicenseTemp.refUser.username", type: "String", operator: "=", value: userInfo.username }]);
        if (userInfo.isCeo) {
            whereGroup.push([{ name: "refHazLicenseTemp.refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id }]);
        }
    }
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([{ name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username }]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ name: "refStatus.key", type: "String", operator: "!=", value: "draft" }]);
    }
    return [
        {
            name: "refRequestDoc.key",
            type: "String",
            operator: "in",
            value: has({ menuContext, key: "forHazLicenseNotice" }) ? ["12", "13", "14", "15"] : ["08", "09", "10", "11"]
        },
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ];
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        isSignCa: null,
        refHazLicense: null,
        refHazLicenseTemp: { ...createFormHazLicense() },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            waitReceive: [{ remark: null, statusDatetime: null }],
            waitApprove: [{ remark: null, statusDatetime: null }],
            approve: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forReceiveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ props, data, refs, key }) => {
    return {
        ...createRulesTop({ props, data, refs, key }),
        ...createRulesTab1({ props, data, refs, key }),
        ...createRulesTab2({ props, data, refs, key }),
        ...createRulesTab3({ props, data, refs, key }),
        ...createRulesTab5({ props, data, refs, key })
    };
};

export const createRulesTop = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDocGenType`]: [required],
        [`${prefix}requestCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refHazLicenseTemp.approveCode`]: [requiredWhen({ when: () => ["2"].includes(model.refDocGenType?.key) })],
        [`${prefix}refRequestType`]: [required],
        [`${prefix}refHazLicense`]: [requiredWhen({ when: () => ["2", "3", "4"].includes(model.refRequestType?.key) })],
        [`${prefix}refHazLicenseTemp.refOperation`]: [required],
        [`${prefix}refHazLicenseTemp.quantity`]: [required],
        [`${prefix}refHazLicenseTemp.refQuantityUnit`]: [required],
        [`${prefix}refHazLicenseTemp.specificArea`]: [requiredWhen({ when: () => ["5"].includes(modelRef.refHazLicenseTemp?.refOperation?.key) })],
        [`${prefix}refHazLicenseTemp.refHazLicenseObjective`]: [
            requiredWhen({ when: () => ["5"].includes(modelRef.refHazLicenseTemp?.refOperation?.key) })
        ],
        [`${prefix}refHazLicenseTemp.transportation`]: [
            requiredWhen({ when: () => ["1"].includes(modelRef.refHazLicenseTemp?.refHazLicenseObjective?.key) })
        ],
        [`${prefix}refHazLicenseTemp.hazLicenseCountries`]: [
            requiredWhen({ when: () => ["2"].includes(modelRef.refHazLicenseTemp?.refOperation?.key) })
        ],
        [`${prefix}lastStatus.forRequestRemark.0.remark`]: [requiredWhen({ when: () => !model.isOfficerCreate && ["2", "4"].includes(model.refRequestType?.key) && (!model.id || ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(model.refStatus?.key)) })]
    };
};

export const createRulesTab1 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refDepartment`]: [required],
        [`${prefix}refHazLicenseTemp.refUserCompany`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refHazLicenseTemp.refUserCompany?.id })
        ],
        [`${prefix}refHazLicenseTemp.refUser`]: [
            requiredWhen({ when: () => true, requiredValidator: () => model.refHazLicenseTemp.refUser?.id })
        ]
    };
};

export const createRulesTab2 = ({ key }) => {
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refHazLicenseTemp.hazLicenseDetails`]: [required]
    };
};

export const createRulesTab3 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refHazLicenseTemp.hazLicenseStorageLocations`]: [requiredWhen({ when: () => {
            const storageLocationSize = model.refHazLicenseTemp.hazLicenseStorageLocations.filter(d => !d.isDelete && d.refStorageLocation.isLocation === true).length;
            const manufactureSize = model.refHazLicenseTemp.hazLicenseStorageLocations.filter(d => !d.isDelete && d.refStorageLocation.isLocation === false).length;
            if(model.refHazLicenseTemp.refOperation?.key === "4") {
                return storageLocationSize === 0 || manufactureSize === 0;
            }
            return storageLocationSize === 0;
        }} )]
    };
};

export const createRulesTab31 = ({ data, key, isLocation }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refHazLicenseTemp.hazLicenseStorageLocations`]: [
            requiredWhen({
                when: () => {
                    return model.refHazLicenseTemp.hazLicenseStorageLocations.filter(d => !d.isDelete && d.refStorageLocation.isLocation === isLocation).length === 0;
                }
            }),
            {
                validator: () => {
                    if (model?.refStatus?.key === 'draft' && ["1", "2", "3"].includes(modelRef.refRequestType?.key)) {
                        const hazLicenseStorageLocations = model.refHazLicenseTemp?.hazLicenseStorageLocations?.filter?.(d => !d.isDelete && d.refStorageLocation.isLocation === isLocation) || []
                        if (hazLicenseStorageLocations) {
                            const isExpired = hazLicenseStorageLocations.some?.(d => moment().isAfter(d.refStorageLocation?.expireDate))
                            if (isExpired) {
                                return Promise.reject(i18n.global.t("error.form.expireDate"));
                            }
                        }
                    }
                    return Promise.resolve();
                },
                required: true,
                trigger: "change"
            }
        ]
    };
};

export const createRulesTab5 = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.refHazLicenseTemp?.attachments?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}refHazLicenseTemp.attachments.${index}.documentName`] = [required];
            rules[`${prefix}refHazLicenseTemp.attachments.${index}.size`] = [
                requiredWhen({ when: () => true && (model.refHazLicenseTemp.attachments[index].attribute?.isRequiredAttachment || model.refHazLicenseTemp.attachments[index].attribute?.documentNo) })
            ];
        }
    });
    return rules;
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault, routerPrefix } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [callUserObjective({}), callRequestDoc({}), callRequestStatus({}), callRequestType({}), callSpeciesByGroup({}), callRequestDocGenType({})];
    return Promise.all(promises).then(arr => {
        data.userObjectives = arr[0];
        data.requestDocs = arr[1];
        data.statuses = arr[2];
        data.requestType = arr[3];
        data.requestSpecies = arr[4];
        data.requestDocGenTypes = arr[5];
        const userInfo = store.state.resource.auth.info?.user;

        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            if (!modelRef.isOfficerCreate) {
                callUserCompanyMe({}).then(userCompanies => {
                    const refUserCompany = userCompanies[0];
                    modelRef.refHazLicenseTemp.refUserCompany = refUserCompany;
                    if (refUserCompany) {
                        callUser({ userCompanyId: refUserCompany.id }).then(
                            users => (modelRef.refHazLicenseTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id))
                        );
                        modelRef.refHazLicenseTemp.refOperation = checkUserCompanyOperation({
                            operations: data.userObjectives,
                            userCompany: refUserCompany
                        })[0];
                        data.onUpdateRefOperation?.(modelRef.refHazLicenseTemp.refOperation);
                    }
                });
            }
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === (routerPrefix === "haz-license-requests" ? "08" : "12"));
            modelRef.refRequestType = data.requestType.find(d => d.key === "1");
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");
            modelRef.refHazLicenseTemp.refSpecies = data.requestSpecies.find(d => d.key === (routerPrefix === "haz-license-requests" ? "3" : "2"));
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction.canReStatus({ props, record })
        }),
        /*
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportHazLicense({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
        */
        createRowActionOpenUrl({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callUrl: ({ record }) => `${process.env.VUE_APP_API_REPORT_URL}/files/haz/company/${record.id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`,
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    data.userInfo = store.state.resource.auth.info?.user;
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.refHazLicenseTemp.hazLicenseStorageLocations.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesTab3({ props, data, refs }),
        itemsKey: "refHazLicenseTemp.hazLicenseStorageLocations"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs, state })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive, feeStatements } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canHsss = state.conditionAction.canHsss({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refHazLicenseTemp = modelRef.refHazLicenseTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forReceiveRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        formParams: {
                            refHazLicenseTemp: {
                                ...modelRef.refHazLicenseTemp,
                                refUserCompany: refHazLicenseTemp?.refUserCompany?.id ? refHazLicenseTemp?.refUserCompany : null,
                                refUser: refHazLicenseTemp?.refUser?.id ? refHazLicenseTemp?.refUser : null
                            },
                            statusRemark
                        }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmRequest({
                            onOk: record => {
                                const refHazLicenseTemp = modelRef.refHazLicenseTemp;
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refHazLicenseTemp: {
                                            ...modelRef.refHazLicenseTemp,
                                            refUserCompany: refHazLicenseTemp?.refUserCompany?.id ? refHazLicenseTemp?.refUserCompany : null,
                                            refUser: refHazLicenseTemp?.refUser?.id ? refHazLicenseTemp?.refUser : null
                                        },
                                        statusRemark: modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: modelRef.lastStatus.forRequestRemark[0].remark,
                                        refStatus: statusWaitReceive,
                                        isSignCa: record.isSignCa,
                                        passwordCa: record.passwordCa
                                    },
                                    successText: i18n.global.t("views.request.modal.request.sendSuccess")
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmReceive({
                            props: {
                                roleName: has({ menuContext, key: "forHazLicenseNotice" }) ? "HAZ_LICENSE_NOTICE_APPROVE" : "HAZ_LICENSE_APPROVE",
                                statusKeys: ["draft"].includes(initialModel.refStatus?.key) ? ["waitApprove"] : []
                            },
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack
                                            ? modelRef.lastStatus.forReceiveRemark[0].remark
                                            : null,
                                        refStatus: record.refStatus,
                                        refToApproveBy: record.refToApproveBy
                                    },
                                    successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forReceiveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack
                                            ? modelRef.lastStatus.forReceiveRemark[0].remark
                                            : null,
                                        refStatus: record.refStatus
                                    },
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && canHsss) {
        col2.push(
            createButton({
                text: i18n.global.t("button.hsss"),
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    Modal[modelRef.hsssResponse ? "info" : "error"]({ content: <pre>{(modelRef.hsssResponse ? JSON.stringify(modelRef.hsssResponse) : null) || modelRef.hsssError}</pre>, width: 800 });
                }
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    const feeStatementId = feeStatements?.[0]?.id;
    if (id && feeStatementId && ((
        has({ menuContext, key: "forRequest" }) && ![undefined, "draft"].includes(feeStatements?.[0]?.refStatus?.key)) ||
        have({ menuContext, keys: ["forReceive", "forApprove"] }
    ))) {
        col2.push(
            createButton({
                text: `${i18n.global.t("model.feeStatement.modelName")}${feeStatements?.[0] ? ` (${feeStatements[0].refStatus?.value})` : ""}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    validate({ props, data, refs }).then(() => {
                        router.push({ name: `fees-view`, params: { id: feeStatementId } });
                    });
                },
                icon: <CIcon file="active.svg"></CIcon>
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "*",
                "refHazLicense.*",
                "refHazLicenseTemp.*",
                // "refHazLicenseNew.*",
                "refHazLicenseMax.id",
                "refHazLicenseMax.approveCode",
                "refHazLicenseMax.effectiveDate",
                "refHazLicenseMax.expireDate",
                "refHazLicenseTemp.refUserCompany.*",
                "refHazLicenseTemp.refUserCompany.refPersonType.*",
                "refHazLicenseTemp.refUser.*",
                "refHazLicenseTemp.refOperation.*",
                "refHazLicenseTemp.refSpecies.*",
                "refRequestType.*",
                "refStatus.*"
            ],
            children: [
                {
                    name: "refHazLicenseTemp.hazLicenseDetails",
                    select: ["*", "refHaz.refOperation.*", "refHaz.refHazObject.refSpecies.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }]
                },
            ],
            sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/ { name: "updateDate", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
